import { onMounted, onUnmounted, ref, type Ref } from 'vue';

export interface NativeBridgeApi {
	/** Vrai lorsque l'application tourne dans la coque Android/iOS. */
	isNative: Ref<boolean>;
	platform: Ref<string>;
	/** Demande la permission de localisation ; sans objet sur le web. */
	requestLocationPermission: () => Promise<boolean>;
	/** Retour haptique court, pour confirmer un geste au volant. */
	vibrate: () => Promise<void>;
	/** Notification locale, utile quand l'application est en arrière-plan. */
	notify: (title: string, body: string) => Promise<void>;
}

/**
 * Pont vers les capacités natives.
 *
 * Tous les plugins Capacitor sont chargés **dynamiquement** et chaque appel est
 * protégé : le même code doit fonctionner à l'identique sur le site, dans la
 * PWA et dans la coque native, où seuls les plugins sont réellement présents.
 * Sur le web, ces appels sont sans effet plutôt qu'en erreur.
 */
export function useNativeBridge(): NativeBridgeApi {
	const isNative = ref(false);
	const platform = ref('web');

	let removeResumeListener: (() => void) | null = null;

	const loadCapacitor = async () => {
		try {
			return await import('@capacitor/core');
		} catch {
			return null;
		}
	};

	const requestLocationPermission = async (): Promise<boolean> => {
		if (!isNative.value) {
			// Sur le web, c'est le navigateur qui demande, à la première mesure.
			return true;
		}
		try {
			const { Geolocation } = await import('@capacitor/geolocation');
			const status = await Geolocation.requestPermissions();
			return status.location === 'granted' || status.coarseLocation === 'granted';
		} catch {
			return false;
		}
	};

	const vibrate = async (): Promise<void> => {
		try {
			if (isNative.value) {
				const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
				await Haptics.impact({ style: ImpactStyle.Medium });
			} else if ('vibrate' in navigator) {
				navigator.vibrate?.(60);
			}
		} catch {
			// Retour haptique indisponible : sans conséquence.
		}
	};

	const notify = async (title: string, body: string): Promise<void> => {
		if (!isNative.value) {
			return;
		}
		try {
			const { LocalNotifications } = await import('@capacitor/local-notifications');
			await LocalNotifications.schedule({
				notifications: [
					{
						// Identifiant borné : Android refuse les entiers 64 bits.
						id: Date.now() % 2_147_483_647,
						title,
						body,
						schedule: { at: new Date(Date.now() + 100) },
					},
				],
			});
		} catch {
			// Permission refusée ou plugin absent.
		}
	};

	onMounted(async () => {
		const capacitor = await loadCapacitor();
		if (!capacitor) {
			return;
		}

		isNative.value = capacitor.Capacitor.isNativePlatform();
		platform.value = capacitor.Capacitor.getPlatform();

		if (!isNative.value) {
			return;
		}

		// Barre de statut aux couleurs de la marque, puis retrait de l'écran de
		// démarrage une fois l'interface prête — sans quoi il reste affiché.
		try {
			const { StatusBar, Style } = await import('@capacitor/status-bar');
			await StatusBar.setStyle({ style: Style.Dark });
			await StatusBar.setBackgroundColor({ color: '#C8102E' });
		} catch {
			// Plugin absent (iOS en plein écran, par exemple).
		}

		try {
			const { SplashScreen } = await import('@capacitor/splash-screen');
			await SplashScreen.hide();
		} catch {
			// Sans conséquence.
		}

		// Retour au premier plan : l'application a pu manquer des événements
		// pendant sa mise en veille. On recharge alors l'état côté appelant.
		try {
			const { App } = await import('@capacitor/app');
			const listener = await App.addListener('appStateChange', ({ isActive }) => {
				if (isActive) {
					document.dispatchEvent(new CustomEvent('taxi:resume'));
				}
			});
			removeResumeListener = () => void listener.remove();
		} catch {
			// Sans conséquence.
		}
	});

	onUnmounted(() => {
		removeResumeListener?.();
	});

	return { isNative, platform, requestLocationPermission, vibrate, notify };
}
